import { useCallback, useRef, useState } from 'react'
import { splitQuery } from '@/lib/search-utils'

/**
 * 検索ボックスの操作を管理するカスタムフック
 *
 * トークン削除、キー操作、コンテナクリックのハンドラを提供します
 */
export function useSearchBoxHandlers<T extends { raw: string }>(
  value: string,
  onChange: (value: string) => void,
  tokens: T[],
) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * トークンを削除
   */
  const removeToken = useCallback(
    (tokenToRemove: T) => {
      const parts = splitQuery(value)
      const newParts = parts.filter((p) => p !== tokenToRemove.raw)
      onChange(newParts.join(' '))
    },
    [value, onChange],
  )

  /**
   * キーダウンハンドラ
   *
   * - Enter: 新しいキーワードを追加
   * - Backspace (入力が空): 最後のトークンを削除
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault()
        const newValue = value ? `${value} ${inputValue.trim()}` : inputValue.trim()
        onChange(newValue)
        setInputValue('')
      }
      // Backspaceで最後のトークンを削除
      if (e.key === 'Backspace' && !inputValue && tokens.length > 0) {
        const lastToken = tokens[tokens.length - 1]
        if (lastToken) {
          removeToken(lastToken)
        }
      }
    },
    [inputValue, value, onChange, tokens, removeToken],
  )

  /**
   * コンテナクリックで入力にフォーカス
   */
  const handleContainerClick = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  return {
    inputValue,
    setInputValue,
    inputRef,
    removeToken,
    handleKeyDown,
    handleContainerClick,
  }
}
